import { useState, useEffect } from 'react';
import { galleryImages, type GalleryImage } from '../../../mocks/galleryData';

type FilterType = 'all' | GalleryImage['category'];

const filterLabels: { id: FilterType; label: string; icon: string }[] = [
  { id: 'all', label: 'All', icon: 'ri-grid-line' },
  { id: 'food', label: 'Food', icon: 'ri-restaurant-line' },
  { id: 'mural', label: 'Murals', icon: 'ri-brush-line' },
  { id: 'interior', label: 'Interior', icon: 'ri-home-heart-line' },
  { id: 'drinks', label: 'Drinks', icon: 'ri-goblet-line' },
];

const heightMap: Record<GalleryImage['height'], string> = {
  short: '200px',
  medium: '280px',
  tall: '380px',
};

export default function MasonryGrid() {
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  const filtered = activeFilter === 'all'
    ? galleryImages
    : galleryImages.filter((img) => img.category === activeFilter);

  const categoryLabels: Record<GalleryImage['category'], string> = {
    food: 'Food',
    mural: 'Mural Art',
    interior: 'Interior',
    drinks: 'Drinks',
  };

  return (
    <div>
      {/* Filter Bar */}
      <div className="flex items-center gap-3 overflow-x-auto scrollbar-hide pb-2">
        {filterLabels.map((f) => (
          <button
            key={f.id}
            onClick={() => setActiveFilter(f.id)}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-sans text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
              activeFilter === f.id
                ? 'bg-saffron text-white'
                : 'bg-white text-charcoal/70 border border-charcoal/10 hover:border-saffron hover:text-saffron'
            }`}
          >
            <i className={f.icon}></i>
            {f.label}
            {f.id !== 'all' && (
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${activeFilter === f.id ? 'bg-white/20' : 'bg-charcoal/5'}`}>
                {galleryImages.filter(g => g.category === f.id).length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Masonry Grid */}
      <div
        className="mt-8 columns-2 sm:columns-3 lg:columns-4 gap-4 space-y-4"
        style={{ columnGap: '16px' }}
      >
        {filtered.map((img, i) => (
          <div
            key={img.id}
            className={`relative rounded-2xl overflow-hidden cursor-pointer break-inside-avoid mb-4 transition-all duration-700 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{
              transitionDelay: `${i * 0.05}s`,
              height: heightMap[img.height],
            }}
            onMouseEnter={() => setHoveredId(img.id)}
            onMouseLeave={() => setHoveredId(null)}
          >
            <img
              src={img.src}
              alt={img.caption}
              className={`w-full h-full object-cover transition-transform duration-500 ${
                hoveredId === img.id ? 'scale-110' : 'scale-100'
              }`}
            />
            {/* Hover Overlay */}
            <div
              className={`absolute inset-0 transition-all duration-300 ${
                hoveredId === img.id ? 'bg-black/60' : 'bg-black/0'
              }`}
            ></div>
            {/* Caption */}
            <div
              className={`absolute bottom-0 left-0 right-0 p-4 transition-all duration-300 ${
                hoveredId === img.id ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
              }`}
            >
              <p className="font-sans text-[10px] uppercase tracking-widest text-saffron mb-1">
                {categoryLabels[img.category]}
              </p>
              <p className="font-serif text-white text-lg leading-tight">{img.caption}</p>
            </div>
          </div>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-20">
          <i className="ri-image-line text-5xl text-charcoal/20 block mb-4"></i>
          <p className="font-sans text-charcoal/50">No images in this category yet.</p>
        </div>
      )}
    </div>
  );
}
